import apiClient from '@/services/axios'
import { getErrorMessage } from '@/views/apps/referral-program/composables/useGetErrorMessage.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

const getDefaultState = () => ({
  exportsData: [],
  exportingIds: {},
  removingIds: {},
})

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations: {
    SET_EXPORTS(state, payload) {
      state.exportsData = payload
    },
    ADD_EXPORT(state, exportItem) {
      const exists = state.exportsData.find(e => e.id === exportItem.id)

      if (exists) {
        return
      }

      state.exportsData.unshift(exportItem)
    },
    REMOVE_EXPORT(state, id) {
      state.exportsData = state.exportsData.filter(e => e.id !== id)
    },
    UPDATE_EXPORT(state, payload) {
      const idx = state.exportsData.findIndex(e => e.id === payload.id)
      if (idx !== -1) {
        state.exportsData.splice(idx, 1, {
          ...state.exportsData[idx],
          ...payload,
        })
        return
      }

      state.exportsData.unshift(payload)
    },
    SET_EXPORTING(state, exportId) {
      state.exportingIds = {
        ...state.exportingIds,
        [exportId]: true,
      }
    },
    SET_REMOVING(state, id) {
      state.removingIds[id] = true
    },
    UNSET_EXPORTING(state, exportId) {
      const { [exportId]: _, ...rest } = state.exportingIds
      state.exportingIds = rest
    },
    RESET_STATE(state) {
      Object.assign(state, getDefaultState())
    },
  },
  actions: {
    async getExportsData({ commit }, id) {
      try {
        const response = await apiClient.get(`${referralApiUrl}/referral-programs/${id}/export`)
        if (response?.status >= 200 && response?.status < 300 && response?.data?.data) {
          commit('SET_EXPORTS', response?.data?.data || null)
          return
        }

        getErrorMessage(response?.data)
      } catch (error) {
        console.error('Failed to fetch data:', error)
        commit('SET_EXPORTS', [])
      }
    },
    async removeExport({ commit }, { id, exportId }) {
      try {
        const response = await apiClient.delete(`${referralApiUrl}/referral-programs/${id}/export/${exportId}`)
        if (response?.status >= 200 && response?.status < 300) {
          commit('SET_REMOVING', exportId)

          setTimeout(() => {
            commit('REMOVE_EXPORT', exportId)
          }, 350)
          return
        }

        getErrorMessage(response?.data)
      } catch (error) {
        console.error('Failed to remove export:', error)
      }
    },
    async downloadExport({ commit }, { id, exportId }) {
      try {
        commit('SET_EXPORTING', exportId)
        const response = await apiClient.get(`${referralApiUrl}/referral-programs/${id}/export/${exportId}/download`, {
          responseType: 'blob',
          headers: { Accept: 'application/octet-stream' },
        })

        if (response?.status >= 200 && response?.status < 300) {
          const blob = response.data

          const link = document.createElement('a')
          const objectUrl = URL.createObjectURL(blob)

          link.href = objectUrl

          const disposition = response.headers['content-disposition']
          let filename = 'export.xlsx'

          if (disposition) {
            const match = disposition.match(/filename="?(.+)"?/)
            if (match?.[1]) {
              filename = match[1]
            }
          }

          link.download = filename

          document.body.appendChild(link)
          link.click()

          link.remove()
          URL.revokeObjectURL(objectUrl)

          commit('SET_REMOVING', exportId)

          setTimeout(() => {
            commit('REMOVE_EXPORT', exportId)
          }, 350)
          return
        }

        getErrorMessage(response?.data)
      } catch (error) {
        console.error('Failed to remove export:', error)
      } finally {
        commit('UNSET_EXPORTING', exportId)
      }
    },
    addExport({ commit }, data) {
      commit('ADD_EXPORT', data)
    },
    updateExport({ commit }, data) {
      commit('UPDATE_EXPORT', data)
    },
    resetState({ commit }) {
      commit('RESET_STATE')
    },
  },
  getters: {
    exportsData: (state) => state.exportsData,
    isExporting: (state) => (exportId) => {
      return !!state.exportingIds[exportId]
    },
    isRemoving: state => exportId => {
      return !!state.removingIds[exportId]
    },
  },
}
